import type { PoolRoomState, PoolTournamentState } from "@/shared/types";
import type { PoolBallState } from "@/shared/types/pool";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface PoolResponse {
  state: PoolRoomState;
}

interface PoolTournamentResponse {
  state: PoolTournamentState;
}

function buildApiUrl(path: string): string {
  if (!API_BASE_URL) {
    throw new Error("REACT_APP_API_BASE_URL is not configured");
  }

  const base = API_BASE_URL.replace(/\/+$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const dedupedPath = base.endsWith("/api") && normalizedPath.startsWith("/api/")
    ? normalizedPath.slice(4)
    : normalizedPath;

  return `${base}${dedupedPath}`;
}

async function poolFetch(path: string, idToken: string, options?: RequestInit): Promise<PoolResponse> {
  const response = await fetch(buildApiUrl(path), {
    ...options,
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
      ...(options?.headers || {})
    },
    credentials: "include"
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Pool request failed");
  }

  return response.json();
}

export async function createPoolRoom(idToken: string): Promise<PoolRoomState> {
  const res = await poolFetch("/api/pool/room/create", idToken, { method: "POST" });
  return res.state;
}

export async function joinPoolRoom(roomId: string, idToken: string): Promise<PoolRoomState> {
  const res = await poolFetch("/api/pool/room/join", idToken, {
    method: "POST",
    body: JSON.stringify({ roomId })
  });
  return res.state;
}

export async function fetchPoolRoomState(roomId: string, idToken: string): Promise<PoolRoomState> {
  const res = await poolFetch(`/api/pool/room/state?roomId=${encodeURIComponent(roomId)}`, idToken);
  return res.state;
}

export async function submitPoolShot(
  payload: {
    roomId: string;
    commandId: string;
    directionX: number;
    directionY: number;
    power: number;
    calledPocket?: number;
  },
  idToken: string
): Promise<PoolRoomState> {
  const res = await poolFetch("/api/pool/shot", idToken, {
    method: "POST",
    body: JSON.stringify(payload)
  });
  return res.state;
}

export async function completePoolTurn(
  payload: {
    roomId: string;
    commandId: string;
    keepTurn: boolean;
    foul: boolean;
    cuePocketed: boolean;
    pocketedBalls?: number[];
    eightBallPocket?: number;
    balls?: PoolBallState[];
  },
  idToken: string
): Promise<PoolRoomState> {
  const res = await poolFetch("/api/pool/turn/complete", idToken, {
    method: "POST",
    body: JSON.stringify(payload)
  });
  return res.state;
}

export async function completePoolMatch(
  payload: {
    roomId: string;
    commandId: string;
    winnerUserId: number;
  },
  idToken: string
): Promise<PoolRoomState> {
  const response = await fetch(buildApiUrl("/api/pool/match/complete"), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json"
    },
    credentials: "include",
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Pool match completion failed");
  }

  const data = await response.json();
  return data.roomState as PoolRoomState;
}

async function poolTournamentFetch(path: string, idToken: string, options?: RequestInit): Promise<PoolTournamentResponse> {
  const response = await fetch(buildApiUrl(path), {
    ...options,
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
      ...(options?.headers || {})
    },
    credentials: "include"
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Pool tournament request failed");
  }

  return response.json();
}

export async function createPoolTournament(idToken: string): Promise<PoolTournamentState> {
  const res = await poolTournamentFetch("/api/pool/tournament/create", idToken, { method: "POST" });
  return res.state;
}

export async function joinPoolTournament(tournamentId: string, idToken: string): Promise<PoolTournamentState> {
  const res = await poolTournamentFetch("/api/pool/tournament/join", idToken, {
    method: "POST",
    body: JSON.stringify({ tournamentId })
  });
  return res.state;
}

export async function reportPoolTournamentMatch(
  payload: { tournamentId: string; matchId: string; winnerUserId: number; commandId: string },
  idToken: string
): Promise<PoolTournamentState> {
  const res = await poolTournamentFetch("/api/pool/tournament/report", idToken, {
    method: "POST",
    body: JSON.stringify(payload)
  });
  return res.state;
}

export async function fetchPoolTournamentState(tournamentId: string, idToken: string): Promise<PoolTournamentState> {
  const res = await poolTournamentFetch(`/api/pool/tournament/state?tournamentId=${encodeURIComponent(tournamentId)}`, idToken);
  return res.state;
}
